import type { AreaSwitchBandState } from '~/types/areaSwitchBand';
import type { CountryLanguages } from '~/types/countryLanguages';
import type { StoryblokConfigSlug } from '~/types/storyblokConfigSlug';
import { findSectionConfigForPath } from '~/utils/findSectionConfigForPath';
import { parseLocaleFromPath } from '~/utils/parseLocaleFromPath';
import { resolveSectionStartPath } from '~/utils/resolveSectionSwitchPath';

/**
 * The origin has no meaning. The function only needs an origin to find out if a path stays on the same origin.
 * The `.invalid` top-level domain never exists.
 */
const PLACEHOLDER_ORIGIN = 'https://area-switch-band.invalid';

/**
 * Returns the stored path with query in its normalized form, or `null` when the path is not safe. The path is
 * safe when it is a relative path that stays on the same origin and has the locale segment as its first segment.
 * The result always starts with `/<localeSegment>`. Thus it never starts with `//` and the browser never uses it
 * as a link to a different origin.
 */
function parseStoredPath(storedPath: string, localeSegment: string): URL | null {
    if (!storedPath.startsWith('/') || storedPath.startsWith('//')) return null;

    let url: URL;
    try {
        url = new URL(storedPath, PLACEHOLDER_ORIGIN);
    } catch {
        return null;
    }

    // Example: the URL parser reads `/\evil.example` as `//evil.example`, which is a different origin.
    if (url.origin !== PLACEHOLDER_ORIGIN) return null;

    // The URL parser can remove dot segments. Example: `/.//evil.example` becomes the path `//evil.example`.
    const localePath = `/${localeSegment}`;
    const isInLocale = url.pathname === localePath || url.pathname.startsWith(`${localePath}/`);

    return isInLocale ? url : null;
}

/**
 * Returns the URL of the back link, or `null` when the band must not be visible.
 *
 * The back link goes to the last page of the stored area, when this page is safe, when it is in the current locale,
 * and when it is in the stored area. Otherwise it goes to the start page of the stored area in the current locale.
 * When the area does not exist in the current country, there is no back link.
 *
 * The cookie is user input. Do not trust it.
 */
export function resolveAreaSwitchBandBackUrl(
    state: AreaSwitchBandState | null,
    registry: StoryblokConfigSlug[] | null | undefined,
    countryLanguages: CountryLanguages
): string | null {
    if (!state?.band) return null;

    const { band, current } = state;
    const storedUrl = parseStoredPath(band.path, current.localeSegment);
    const storedPathConfig = storedUrl && findSectionConfigForPath(registry, storedUrl.pathname, countryLanguages);

    if (storedUrl && storedPathConfig?.sectionId === band.area) {
        return `${storedUrl.pathname}${storedUrl.search}`;
    }

    const locale = parseLocaleFromPath(`/${current.localeSegment}`);
    if (!locale?.country || !locale.language) return null;

    return resolveSectionStartPath(registry, band.area, locale.country, locale.language, countryLanguages);
}
